import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  Avatar,
  Button,
  IconButton,
  Spinner,
  Divider,
} from '@chakra-ui/react';
import {
  FiLogIn, FiLogOut, FiInfo, FiMapPin, FiWifiOff, FiWifi, FiExternalLink,
  FiFileText, FiCalendar, FiAlertTriangle, FiUserCheck, FiFileMinus, FiX,
} from 'react-icons/fi';
import { FaFilePdf } from 'react-icons/fa';
import { getCustomersWithOptions } from 'services/woo';
import { getSurveyResponse, API_URL_BASE } from 'services/surveyService';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';
import FichaFieldValue, { buildFileUrl } from 'components/Fichas/fichaFieldRenderer';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';

// `grad` — mismo criterio que ACCESS_TYPE_BG en HistoryPanel.js: un
// degradado suave (estilo WARM_SHELL_BG) en la cabecera del modal, en vez
// del color plano `bg`, con el mismo tono pastel por tipo (mint=entrada,
// butter=salida, sky=consulta).
const ACCESS_TYPE_CONFIG = {
  entry: { icon: FiLogIn,  bg: '#DCF1E6', grad: 'linear-gradient(160deg, #E7F7EE 0%, #C7ECD7 100%)', color: '#1E7A4E', label: 'Entrada'  },
  exit:  { icon: FiLogOut, bg: '#FCE7CC', grad: 'linear-gradient(160deg, #FDF1DD 0%, #F6DBAB 100%)', color: '#B5631A', label: 'Salida'   },
  info:  { icon: FiInfo,   bg: '#E2ECFB', grad: 'linear-gradient(160deg, #EAF2FD 0%, #CEE0FA 100%)', color: '#2A66B4', label: 'Solo info' },
};

const EXIT_REASON_LABELS = {
  pedagogical:   'Salida pedagógica',
  pickup:        'Retiro',
  sick:          'Enfermedad',
  medical:       'Cita médica',
  parent_pickup: 'Retiro apoderado',
  emergency:     'Emergencia',
  authorized:    'Act. autorizada',
  discipline:    'Disciplina',
  other:         'Otro',
};

const STATUS_AT_SCAN_LABELS = {
  suspended:        'Suspendido',
  medical_leave:    'Licencia médica',
  pedagogical_exit: 'Salida pedagógica',
  sick_exit:        'Salida por enfermedad',
};

const openProfile = (id) => {
  if (!id) return;
  window.open(`${window.location.origin}/#/admin/usuario/${id}`, '_blank');
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-CL', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// ── Fila etiqueta/valor del panel de información del movimiento ────────────
const InfoRow = ({ icon, label, children }) => (
  <HStack align="flex-start" spacing="10px">
    <Flex w="34px" h="34px" borderRadius="10px" bg="cream.bg2" align="center" justify="center" flexShrink={0} mt="1px">
      <Icon as={icon} boxSize="16px" color="ink.400" />
    </Flex>
    <VStack align="flex-start" spacing="1px" minW={0} flex={1}>
      <Text fontSize="12px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".04em">
        {label}
      </Text>
      <Box fontSize="14.5px" fontWeight="600" color="ink.900">{children}</Box>
    </VStack>
  </HStack>
);

const Chip = ({ label, bg, color, icon }) => (
  <HStack as="span" display="inline-flex" spacing="5px" px="10px" py="4px" borderRadius="999px"
    bg={bg} color={color} fontSize="13.5px" fontWeight="700" whiteSpace="nowrap">
    {icon && <Icon as={icon} boxSize="12px" />}
    <Text as="span">{label}</Text>
  </HStack>
);

// Modal de detalle de UN registro de /qr/logs — pensado para abrirse desde el
// panel lateral de historial (HistoryPanel, versión simplificada del
// listado completo de Accesos QR > Historial de Accesos). Perfil del alumno
// a la izquierda, información del movimiento a la derecha. Angosto por
// defecto (son solo 2 columnas de contenido) — si el registro trae una
// ficha adjunta, "Ver ficha adjunta" la abre EN EL MISMO modal como una 3ª
// columna a la derecha (mismo patrón que ModalSurveyResponse: click en un
// PDF/archivo → se abre al lado, no en otra ventana encima), y el modal se
// ensancha para darle espacio.
const AccessLogDetailModal = ({ isOpen, onClose, log }) => {
  const [student, setStudent]   = useState(null);
  const [loading, setLoading]   = useState(false);

  // Preview inline de la ficha (3ª columna) — separado del modal completo
  // (fullFichaOpen, ver más abajo) que sigue disponible para PDF/eliminar.
  const [fichaPreviewOpen, setFichaPreviewOpen] = useState(false);
  const [fichaData, setFichaData]     = useState(null);
  const [fichaLoading, setFichaLoading] = useState(false);
  const [fullFichaOpen, setFullFichaOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !log?.rut) { setStudent(null); return; }
    let cancelled = false;
    setLoading(true);
    getCustomersWithOptions({ search: log.rut, role: 'all', per_page: 5 })
      .then(res => {
        if (cancelled) return;
        const found = (res.data ?? []).find(c => c.username === log.rut) ?? null;
        setStudent(found);
      })
      .catch(() => { if (!cancelled) setStudent(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, log?.rut]);

  // Se resetea cada vez que se abre un registro distinto — evita mostrar la
  // ficha del acceso anterior por una fracción de segundo.
  useEffect(() => {
    setFichaPreviewOpen(false);
    setFichaData(null);
  }, [log?.id]);

  const handleToggleFichaPreview = () => {
    if (!log?.survey_response_id) return;
    if (fichaPreviewOpen) { setFichaPreviewOpen(false); return; }
    setFichaPreviewOpen(true);
    if (fichaData) return;
    setFichaLoading(true);
    getSurveyResponse(log.survey_response_id)
      .then(setFichaData)
      .catch(() => setFichaData({ error: 'No se pudieron cargar los datos de la ficha' }))
      .finally(() => setFichaLoading(false));
  };

  if (!log) return null;

  const cfg = ACCESS_TYPE_CONFIG[log.access_type] ?? ACCESS_TYPE_CONFIG.entry;
  const fullName = [student?.first_name, student?.last_name].filter(Boolean).join(' ').trim()
    || log.nombre || log.rut || 'Alumno';
  const course = student?.meta_data?.find(m => m.key === 'group_name')?.value;
  const courseLabel = course ? codeToDisplayLabel(normalizeCourseName(course)) : null;
  const profileId = student?.id ?? log.wp_user_id ?? null;

  // Mismo cálculo de secciones que ModalSurveyResponse (sin los campos
  // meta_data, que ahí quedan detrás de "Mostrar más" — acá directamente se
  // omiten para mantener el panel angosto y de lectura rápida).
  const sectionsMap = (fichaData?.questions || []).reduce((acc, q) => {
    const sectionId = q.section.id;
    if (!acc[sectionId]) acc[sectionId] = { name: q.section.name, questions: [] };
    acc[sectionId].questions.push(q);
    return acc;
  }, {});
  const fichaSections = Object.values(sectionsMap)
    .map(section => ({ ...section, questions: section.questions.filter(q => !q.text.includes('meta_data')) }))
    .filter(section => section.questions.length > 0);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside"
        closeOnOverlayClick={!fichaPreviewOpen}>
        <ModalOverlay />
        <ModalContent fontFamily="jakarta" borderRadius={{ base: 0, md: '22px' }} overflow="hidden"
          maxW={{ base: '100vw', md: fichaPreviewOpen ? '980px' : '600px' }}
          transition="max-width .18s ease">
          <Box bg={cfg.grad} px={6} py={5}>
            <HStack spacing={3}>
              <Flex w="42px" h="42px" borderRadius="12px" bg="white" align="center" justify="center" flexShrink={0}
                boxShadow="0 2px 8px -2px rgba(0,0,0,.12)">
                <Icon as={cfg.icon} boxSize={5} color={cfg.color} />
              </Flex>
              <Box minW={0}>
                <Text fontSize="18.5px" fontWeight="800" color="ink.900" noOfLines={1}>Detalle de acceso</Text>
                <Text fontSize="13.5px" color={cfg.color} fontWeight="700">{cfg.label} · {fmtDateTime(log.scanned_at)}</Text>
              </Box>
            </HStack>
          </Box>
          <ModalCloseButton top="18px" />

          <ModalBody bg="cream.bg" p={0}>
            <Flex direction={{ base: 'column', md: 'row' }} minH={{ md: '280px' }}>
              {/* ── Perfil del alumno (izquierda) ── */}
              <VStack
                w={{ base: '100%', md: '210px' }} flexShrink={0} spacing={4} align="center"
                bg="white" borderRight={{ md: '1px solid' }} borderBottom={{ base: '1px solid', md: 'none' }}
                borderColor="cream.line" px={5} py={7}
              >
                {loading ? (
                  <Spinner size="lg" color="coral.500" />
                ) : (
                  <>
                    <Avatar
                      src={student?.avatar_url || null}
                      name={fullName}
                      size="xl"
                      borderRadius="16px"
                      bg="coral.soft"
                      color="coral.700"
                    />
                    <VStack spacing="4px">
                      <Text fontSize="17.5px" fontWeight="800" color="ink.900" textAlign="center" noOfLines={2}>
                        {fullName}
                      </Text>
                      <Text fontSize="13px" color="ink.400" fontFamily="mono" fontWeight="600">RUT {log.rut}</Text>
                      {courseLabel && (
                        <Chip label={courseLabel} bg="coral.tint" color="coral.700" />
                      )}
                    </VStack>
                    {profileId && (
                      <Button
                        size="md" borderRadius="10px" variant="outline" borderColor="cream.line2"
                        color="ink.700" leftIcon={<FiExternalLink size={14} />}
                        _hover={{ borderColor: 'coral.500', color: 'coral.600' }}
                        onClick={() => openProfile(profileId)}
                      >
                        Ver perfil
                      </Button>
                    )}
                  </>
                )}
              </VStack>

              {/* ── Información del movimiento (centro) ── */}
              <VStack align="stretch" spacing="16px" flex={1} minW={0} px={6} py={6}
                borderRight={fichaPreviewOpen ? { md: '1px solid' } : 'none'} borderColor="cream.line">
                <HStack spacing="8px" flexWrap="wrap">
                  <Chip icon={cfg.icon} label={cfg.label} bg={cfg.bg} color={cfg.color} />
                  {log.is_offline ? (
                    <Chip icon={FiWifiOff} label="Capturado offline" bg="butter.bg" color="butter.ink" />
                  ) : (
                    <Chip icon={FiWifi} label="Online" bg="mint.bg" color="mint.ink" />
                  )}
                  {log.early_exit && (
                    <Chip icon={FiAlertTriangle}
                      label={`Salida anticipada${log.exit_reason ? ` — ${EXIT_REASON_LABELS[log.exit_reason] ?? log.exit_reason}` : ''}`}
                      bg="#FEF3C7" color="#92400E" />
                  )}
                  {log.status_at_scan && (
                    <Chip label={STATUS_AT_SCAN_LABELS[log.status_at_scan] ?? log.status_at_scan} bg="#EDE9F8" color="#6D28D9" />
                  )}
                </HStack>

                <Divider borderColor="cream.line" />

                <InfoRow icon={FiCalendar} label="Fecha y hora">{fmtDateTime(log.scanned_at)}</InfoRow>
                <InfoRow icon={FiMapPin} label="Lector / ubicación">
                  {log.scanner_id ?? '—'}{log.location ? ` · ${log.location}` : ''}
                </InfoRow>
                {log.guardian_present !== null && log.guardian_present !== undefined && (
                  <InfoRow icon={FiUserCheck} label="Apoderado presente">
                    {log.guardian_present ? 'Sí' : 'No'}
                  </InfoRow>
                )}
                {log.exit_notes && (
                  <InfoRow icon={FiFileMinus} label="Notas">{log.exit_notes}</InfoRow>
                )}

                {log.survey_response_id && (
                  <Button
                    mt="4px" alignSelf="flex-start" size="md" borderRadius="10px"
                    bg={fichaPreviewOpen ? 'coral.tint' : 'coral.500'}
                    color={fichaPreviewOpen ? 'coral.700' : '#fff'}
                    fontWeight="700" leftIcon={<FiFileText size={14} />}
                    _hover={{ bg: fichaPreviewOpen ? 'coral.soft' : 'coral.600' }}
                    onClick={handleToggleFichaPreview}
                  >
                    {fichaPreviewOpen ? 'Ocultar ficha adjunta' : 'Ver ficha adjunta'}
                  </Button>
                )}
              </VStack>

              {/* ── Preview de la ficha adjunta (derecha, solo si se abre) ── */}
              {fichaPreviewOpen && (
                <VStack align="stretch" spacing="12px" w={{ base: '100%', md: '340px' }} flexShrink={0}
                  bg="cream.bg2" px={5} py={6} overflowY="auto" maxH={{ md: '70vh' }}>
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="800" fontSize="14.5px" color="ink.900" noOfLines={1} flex={1}>
                      {fichaData?.survey?.name || 'Ficha adjunta'}
                    </Text>
                    <IconButton
                      icon={<FiX />} aria-label="Cerrar ficha" size="xs" variant="ghost"
                      onClick={() => setFichaPreviewOpen(false)}
                    />
                  </Flex>

                  {fichaLoading ? (
                    <Flex justify="center" py={8}><Spinner color="coral.500" /></Flex>
                  ) : fichaData?.error ? (
                    <Text fontSize="13.5px" color="danger.500">{fichaData.error}</Text>
                  ) : (
                    <>
                      {fichaSections.map((section, i) => (
                        <Box key={i} bg="white" border="1px solid" borderColor="cream.line" borderRadius="12px" p="12px 14px">
                          <Text fontSize="11.5px" fontWeight="800" color="ink.400" textTransform="uppercase"
                            letterSpacing=".05em" mb="8px">
                            {section.name}
                          </Text>
                          <VStack align="stretch" spacing="10px">
                            {section.questions.map((q, qi) => (
                              <Box key={qi}>
                                <Text fontSize="13px" fontWeight="700" color="ink.700" mb="4px">{q.text}</Text>
                                <FichaFieldValue
                                  question={q}
                                  apiUrlBase={API_URL_BASE}
                                  onPreviewFile={(filename) => window.open(buildFileUrl(filename, API_URL_BASE), '_blank')}
                                />
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      ))}
                      <Button
                        size="sm" variant="ghost" color="coral.600" leftIcon={<FaFilePdf size={13} />}
                        alignSelf="flex-start" onClick={() => setFullFichaOpen(true)}
                      >
                        Abrir ficha completa / PDF
                      </Button>
                    </>
                  )}
                </VStack>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <ModalSurveyResponse
        isOpen={fullFichaOpen}
        onClose={() => setFullFichaOpen(false)}
        customer={{ first_name: fullName, last_name: '', username: log.rut, id: profileId }}
        data={fichaData}
      />
    </>
  );
};

export default AccessLogDetailModal;
